import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import type { IPhoneCall } from '../../extViewmodels';
import { AIConversation } from '../../extViewmodels/sdk/models/AI';
import { useUserSession } from '../../models/hooks/appStateHooks';

const BY_ID_QUERY_KEY = 'sales-live-coaching-conversation-by-id';

const isNotFoundOrForbidden = (error: Error) => {
	const code = (error as { systemCode?: number }).systemCode;
	return code === 404 || code === 403;
};

/**
 * A coaching conversation paired server-side with the phone call it came from (and that call's
 * resolved company + contacts). `phoneCall` is null when a conversation has no linked call.
 */
export interface AIConversationCallView {
	conversation: AIConversation;
	phoneCall: IPhoneCall | null;
	company: { id?: string; companyName?: string } | null;
	contacts: Api.IContact[] | null;
}

type ByIdQueryParams = {
	id: string;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
};

/**
 * Fetches a single coaching conversation's call view by id via `GET aida/coffee/coaching/{id}`.
 * Disabled when `id` is empty.
 */
export function useSalesLiveCoachingConversationByIdQuery(options: ByIdQueryParams) {
	const userSession = useUserSession();
	const { id, impersonationContext } = options;

	const impersonationKey = impersonationContext
		? { accountId: impersonationContext.account?.id, userId: impersonationContext.user?.id }
		: undefined;

	return useQuery({
		queryKey: [BY_ID_QUERY_KEY, id, impersonationKey],
		queryFn: () =>
			userSession.webServiceHelper.callAsync<AIConversationCallView>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `aida/coffee/coaching/${id}`,
					impersonationContext,
				}),
				'GET'
			),
		enabled: (options.enabled ?? true) && Boolean(id),
		meta: {
			// A 404 (id not found / not a coaching conversation) renders an inline not-found state.
			toastErrorMessage: (error: Error) => (isNotFoundOrForbidden(error) ? '' : 'Failed to load coaching conversation'),
			errorLogging: (error: Error) =>
				isNotFoundOrForbidden(error)
					? undefined
					: { category: 'AIConversation', action: 'SalesLiveCoachingConversationByIdQuery-Error' },
		},
	});
}
